// Imports

// Vuetify
import { useDisplay } from "vuetify/lib/composables/display.mjs";

// Constants
import { getFlagsHeaders } from "../../../constants/tableHeaders";

// Types
import type { FlagItem } from "../../../services/api/domains/pending/pending.types";

/**
 * Flags Dialog Composable
 *
 * @param key - Unique key for state isolation
 */
export const useFlagsDialog = (key: string = "default") => {
  //
  // Composables
  //

  // Localization
  const { t } = useI18n();

  // Breakpoints
  const { smAndDown } = useDisplay();

  //
  // State
  //
  const isOpen = useState<boolean>(`flagsDialog.${key}.isOpen`, () => false);
  const flags = useState<FlagItem[]>(`flagsDialog.${key}.flags`, () => []);

  //
  // Computed Properties
  //
  const flagsHeaders = computed(() => getFlagsHeaders(t, smAndDown.value));

  //
  // Methods
  //
  const open = (flagItems: FlagItem[]) => {
    flags.value = flagItems;
    isOpen.value = true;
  };

  const close = () => {
    isOpen.value = false;
    // flags are intentionally NOT cleared here — clearing synchronously with
    // isOpen causes the empty-state to flash during the dialog close animation.
    // flags will be replaced by the next open() call.
  };

  //
  // Component Props
  //
  const componentProps = computed(() => ({
    isOpen: isOpen.value,
    flags: flags.value,
    flagsHeaders: flagsHeaders.value,
    onClose: close,
  }));

  return {
    // State (readonly)
    isOpen: readonly(isOpen),
    flags: readonly(flags),

    // Computed
    flagsHeaders: readonly(flagsHeaders),
    componentProps,

    // Methods
    open,
    close,
  };
};
